import { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { AuthTokens } from '../types/auth.types';
import { UserInfo, JWTDecoder } from '../types/jwt.types';
import { createAuthError } from '../types/errors.types';
import { TokenStorageService, AuthMethod } from './tokenStorageService';

export interface AuthenticationResult {
  tokens: AuthTokens;
  userInfo: UserInfo;
  account: AccountInfo;
}

export class AuthenticationService {
  private static instance: AuthenticationService;
  private msalInstance: IPublicClientApplication | null = null;

  private constructor() {}

  static getInstance(): AuthenticationService {
    if (!this.instance) {
      this.instance = new AuthenticationService();
    }
    return this.instance;
  }

  initialize(msalInstance: IPublicClientApplication): void {
    this.msalInstance = msalInstance;
  }

  private ensureInitialized(): IPublicClientApplication {
    if (!this.msalInstance) {
      throw createAuthError('Authentication service not initialized', 'AUTH_FAILED');
    }
    return this.msalInstance;
  }

  async acquireTokenSilently(scopes: string[]): Promise<AuthenticationResult> {
    const instance = this.ensureInitialized();
    
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
      throw createAuthError('No authenticated accounts found', 'AUTH_FAILED');
    }

    const account = accounts[0];
    const silentRequest: SilentRequest = {
      scopes,
      account
    };

    try {
      const response = await instance.acquireTokenSilent(silentRequest);
      
      if (!response.idToken) {
        throw createAuthError('No ID token received', 'AUTH_FAILED');
      }

      const userInfo = JWTDecoder.extractUserInfo(response.idToken);
      if (!userInfo) {
        throw createAuthError('Failed to extract user information from token', 'AUTH_FAILED');
      }

      const tokens: AuthTokens = {
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: '',
        expiresIn: response.expiresOn ? 
          Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 3600,
        tokenType: 'Bearer'
      };

      return {
        tokens,
        userInfo,
        account
      };
    } catch (error) {
      if (error instanceof Error) {
        throw createAuthError(
          `Silent token acquisition failed: ${error.message}`,
          'TOKEN_EXPIRED',
          { originalError: error.message }
        );
      }
      throw createAuthError('Silent token acquisition failed', 'TOKEN_EXPIRED');
    }
  }

  async handleAuthenticationResponse(): Promise<AuthenticationResult | null> {
    const instance = this.ensureInitialized();
    
    try {
      const response = await instance.handleRedirectPromise();
      
      if (!response) {
        return null; // No redirect response to handle
      }

      if (!response.idToken) {
        throw createAuthError('No ID token received from redirect', 'AUTH_FAILED');
      }

      const userInfo = JWTDecoder.extractUserInfo(response.idToken);
      if (!userInfo) {
        throw createAuthError('Failed to extract user information from token', 'AUTH_FAILED');
      }

      const tokens: AuthTokens = {
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: '',
        expiresIn: response.expiresOn ? 
          Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 3600,
        tokenType: 'Bearer'
      };

      return {
        tokens,
        userInfo,
        account: response.account!
      };
    } catch (error) {
      if (error instanceof Error) {
        throw createAuthError(
          `Redirect authentication failed: ${error.message}`,
          'AUTH_FAILED',
          { originalError: error.message }
        );
      }
      throw createAuthError('Redirect authentication failed', 'AUTH_FAILED');
    }
  }

  isUserAuthenticated(): boolean {
    const instance = this.ensureInitialized();
    return instance.getAllAccounts().length > 0;
  }

  getCurrentAccount(): AccountInfo | null {
    const instance = this.ensureInitialized();
    const accounts = instance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  async logout(): Promise<void> {
    const instance = this.ensureInitialized();
    
    try {
      TokenStorageService.clearAuthData();
      await instance.logoutRedirect();
    } catch (error) {
      // Even if logout fails, clear local data
      TokenStorageService.clearAuthData();
      if (error instanceof Error) {
        throw createAuthError(
          `Logout failed: ${error.message}`,
          'AUTH_FAILED',
          { originalError: error.message }
        );
      }
      throw createAuthError('Logout failed', 'AUTH_FAILED');
    }
  }

  saveAuthenticationData(result: AuthenticationResult, authMethod: AuthMethod): void {
    TokenStorageService.storeAuthData(result.tokens, result.userInfo, authMethod);
  }

  getStoredAuthenticationData(): {
    tokens: AuthTokens;
    userInfo: UserInfo;
    authMethod: AuthMethod;
  } | null {
    return TokenStorageService.getValidAuthData();
  }

  clearStoredAuthenticationData(): void {
    TokenStorageService.clearAuthData();
  }
}